import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { Radio } from "lucide-react";
import { Api } from "../lib/api.js";
import { getActiveProfileId } from "../lib/profile.js";

/**
 * Live Now page — filterable channel grid grouped by category, with the
 * currently-airing program shown when XMLTV EPG data is available for the
 * channel (joined by tvg_id on the backend).
 *
 * Category names from M3U mix geographic ("US", "Russia", "UK") and topical
 * ("Sports", "Entertainment") — we just expose them as-is in chips since
 * that's how the provider classifies them.
 */
export function LiveNow(): JSX.Element {
  const profileId = getActiveProfileId();
  const [category, setCategory] = useState<string | null>(null);

  const catsQ = useQuery({
    queryKey: ["live-categories"],
    queryFn: () => Api.live.categories(),
  });

  const channelsQ = useQuery({
    queryKey: ["live", profileId, category],
    queryFn: () => Api.live.list(profileId!, category ?? undefined),
    enabled: !!profileId,
  });

  const channels = channelsQ.data?.items ?? [];

  if (!profileId) {
    return (
      <div>
        <h1 className="font-display text-3xl text-ivory">Live Now</h1>
        <div className="ornament-rule" />
        <div className="card p-8 text-parchment">Pick a profile first.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl text-ivory">Live Now</h1>
        <span className="section-label">
          {channelsQ.isLoading ? "…" : `${channels.length} channels`}
        </span>
      </div>
      <div className="ornament-rule" />

      {/* Category chips */}
      <div className="mb-6 flex flex-wrap gap-2">
        <CategoryChip
          label="All"
          count={catsQ.data?.categories.reduce((sum, c) => sum + c.count, 0)}
          active={category === null}
          onClick={() => setCategory(null)}
        />
        {catsQ.data?.categories.map((c) => (
          <CategoryChip
            key={c.name}
            label={c.name}
            count={c.count}
            active={category === c.name}
            onClick={() => setCategory(c.name)}
          />
        ))}
      </div>

      {channelsQ.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Array.from({ length: 12 }).map((_, i) => <div key={i} className="skeleton h-20" />)}
        </div>
      ) : channels.length === 0 ? (
        <div className="card p-12 text-parchment text-center font-display tracking-smallcaps">
          No channels{category ? ` in "${category}"` : ""}.
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {channels.map((c) => <ChannelRow key={c.id} channel={c} />)}
        </ul>
      )}
    </div>
  );
}

function CategoryChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        "px-3 py-1.5 rounded-full text-sm border transition-colors tabular-nums",
        active
          ? "bg-vermilion border-goldHi text-ivory"
          : "border-hairline text-parchment hover:text-ivory hover:bg-elev",
      )}
    >
      {label}
      {count !== undefined && (
        <span className={clsx("ml-2 text-[11px]", active ? "text-goldHi" : "text-parchment/60")}>
          {count}
        </span>
      )}
    </button>
  );
}

interface ChannelData {
  id: string;
  title: string;
  thumbnail_url: string | null;
  category: string | null;
  now_title: string | null;
  now_start: string | null;
  now_stop: string | null;
  next_title: string | null;
  next_start: string | null;
}

function ChannelRow({ channel }: { channel: ChannelData }): JSX.Element {
  const hasEpg = !!channel.now_title;
  const nowPct = nowProgressPct(channel.now_start, channel.now_stop);
  return (
    <li>
      <Link
        to={`/watch/${encodeURIComponent(channel.id)}`}
        className="card flex items-center gap-3 p-3 hover:bg-elev/60 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi"
      >
        {channel.thumbnail_url ? (
          <img
            src={channel.thumbnail_url}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-16 h-16 object-contain bg-ink rounded p-1 flex-shrink-0"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
          />
        ) : (
          <div className="w-16 h-16 bg-elev rounded flex-shrink-0 flex items-center justify-center">
            <Radio size={20} className="text-parchment/40" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-ivory truncate font-medium">{channel.title}</div>
          {hasEpg ? (
            <>
              <div className="text-goldHi text-sm mt-0.5 truncate">
                Now: <span className="text-ivory">{channel.now_title}</span>
              </div>
              {channel.next_title && (
                <div className="text-parchment text-xs mt-0.5 truncate tabular-nums">
                  Next at {fmtTime(channel.next_start)}: {channel.next_title}
                </div>
              )}
              {nowPct !== null && (
                <div className="h-1 mt-2 bg-hairline rounded overflow-hidden">
                  <div className="h-full bg-jade" style={{ width: `${nowPct}%` }} />
                </div>
              )}
            </>
          ) : (
            <div className="text-parchment/50 text-xs mt-0.5 italic">no guide data</div>
          )}
        </div>
        <span
          className="text-[10px] uppercase tracking-smallcaps font-display px-2 py-0.5 rounded-full border whitespace-nowrap"
          style={{
            color: hasEpg ? "#2C6E5B" : "#A89A7C",
            borderColor: hasEpg ? "#2C6E5B55" : "#3B1F1D",
            background: "rgba(15,6,6,0.5)",
          }}
        >
          {channel.category ?? "—"}
        </span>
      </Link>
    </li>
  );
}

function nowProgressPct(start: string | null, stop: string | null): number | null {
  if (!start || !stop) return null;
  const s = new Date(start).getTime();
  const e = new Date(stop).getTime();
  const now = Date.now();
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
  const pct = Math.max(0, Math.min(100, ((now - s) / (e - s)) * 100));
  return Math.round(pct);
}

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}
